import type { Pluggable } from 'unified';

type Node = {
  children?: Node[];
  type: string;
  value?: string;
  data?: {
    hName?: string;
    hProperties?: Record<string, unknown>;
  };
};


export const remarkCitations: Pluggable = () => {
  const MinCitationsToGroup = 5;

  // Helper to collect consecutive citations starting from a given index
  const collectConsecutiveCitations = (matches: RegExpMatchArray[], startIndex: number, firstMatch: RegExpMatchArray): { citations: string[]; nextIndex: number } => {
    const citations: string[] = [];
    let nextIndex = startIndex;
    
    while (
      nextIndex < matches.length &&
      matches[nextIndex].index === matches[startIndex].index + (nextIndex - startIndex) * firstMatch[0].length
    ) {
      citations.push(matches[nextIndex][1]);
      nextIndex++;
    }
    
    return { citations, nextIndex };
  };

  // Helper to create citation nodes
  const createCitationNodes = (citations: string[], matches: RegExpMatchArray[], startIndex: number, endIndex: number): Node[] => {
    if (citations.length >= MinCitationsToGroup) {
      return [{
        type: 'citations',
        data: {
          hName: 'citations',
          hProperties: {
            values: citations,
          },
        },
      } as Node];
    }
    
    const nodes: Node[] = [];
    for (let j = startIndex; j < endIndex; j++) {
      nodes.push({
        type: 'citation',
        data: {
          hName: 'citation',
          hProperties: {
            value: matches[j][1],
          },
        },
      } as Node);
    }
    return nodes;
  };

  // Helper to add text node if needed
  const addTextNodeIfNeeded = (children: Node[], nodeValue: string, lastIndex: number, start: number) => {
    if (start > lastIndex) {
      children.push({
        type: 'text',
        value: nodeValue.slice(lastIndex, start),
      } as Node);
    }
  };

  // Helper to process text node with citations
  const processTextWithCitations = (node: Node): Node[] => {
    const citationPattern = /\^(\d+[a-z]*)\^/g;
    const matches = Array.from(node.value.matchAll(citationPattern));
    const children: Node[] = [];
    let lastIndex = 0;
    let i = 0;

    while (i < matches.length) {
      const match = matches[i];
      const start = match.index;
      const end = start + match[0].length;

      addTextNodeIfNeeded(children, node.value, lastIndex, start);

      // Collect consecutive citations
      const { citations, nextIndex } = collectConsecutiveCitations(matches, i, match);
      
      // Create appropriate citation nodes
      const citationNodes = createCitationNodes(citations, matches, i, nextIndex);
      children.push(...citationNodes);

      lastIndex = end + (nextIndex - i - 1) * match[0].length;
      i = nextIndex;
    }

    if (lastIndex < node.value.length) {
      children.push({
        type: 'text',
        value: node.value.slice(lastIndex),
      } as Node);
    }

    return children;
  };

  return tree => {
    const visit = (node: Node) => {
      if (node.type === 'text' && /\^\d+[a-z]*\^/.test(node.value)) {
        return processTextWithCitations(node);
      }
      return [node];
    };

    const transformer = (node: Node) => {
      if (Array.isArray(node.children)) {
        node.children = node.children.flatMap((child) =>
          visit(child).flatMap(transformer),
        );
      }
      return node;
    };

    const transformed = transformer(tree);
    return transformed;
  };
};